import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronRight,
  Clock3,
  ExternalLink,
  History,
  Link2,
  MapPin,
  NotebookText,
  Pencil,
  UserRound,
  Video,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import type {
  InterviewSchedule,
  InterviewerOption,
  SaveInterviewPayload,
} from '../../libs/api/interviewService';
import InterviewerCombobox from './InterviewerCombobox';
import type { PipelineCard } from './kanbanShared';
import { getInterviewRoundBadgeClass } from '../candidates/candidateInterviewUtils';

interface InterviewScheduleModalProps {
  candidate: PipelineCard | null;
  schedules: InterviewSchedule[];
  interviewers: InterviewerOption[];
  loading: boolean;
  saving: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (payload: SaveInterviewPayload, scheduleId?: string) => Promise<void>;
}

const localDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const meetingRooms = ['Meeting room 1', 'Meeting room 2', 'Meeting room 3'];
const interviewRounds = ['Round 1', 'Round 2', 'Round 3', 'Final round'];
const defaultInterviewRound = 'Round 1';

const roundCode = (round: string) => {
  const match = round.match(/\d+/);
  return match ? `R${match[0]}` : round.slice(0, 2).toUpperCase();
};

const formatScheduleDate = (value: string) => {
  const date = new Date(value);
  return localDate(date);
};

const formatScheduleTime = (value: string) => {
  const date = new Date(value);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const initialDraft = (interviewRound = defaultInterviewRound): SaveInterviewPayload => ({
  interview_round: interviewRound,
  interview_type: 'ONLINE',
  interview_result: null,
  interview_date: localDate(new Date()),
  start_time: '09:00',
  duration_minutes: 30,
  interviewer_user_ids: [],
  meeting_room: null,
  note: null,
});

const scheduleToDraft = (schedule: InterviewSchedule): SaveInterviewPayload => {
  const date = new Date(schedule.start_at);
  return {
    interview_round: schedule.interview_round,
    interview_type: schedule.interview_type,
    interview_result: schedule.interview_result,
    interview_date: localDate(date),
    start_time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
    duration_minutes: schedule.duration_minutes,
    interviewer_user_ids:
      schedule.interviewers
        ?.map((interviewer) => interviewer.id)
        .filter((id): id is string => Boolean(id)) ??
      (schedule.interviewer.id ? [schedule.interviewer.id] : []),
    meeting_room: schedule.meeting_room,
    note: schedule.note,
  };
};

export default function InterviewScheduleModal({
  candidate,
  schedules,
  interviewers,
  loading,
  saving,
  open,
  onOpenChange,
  onSave,
}: InterviewScheduleModalProps) {
  const [activeTab, setActiveTab] = useState('schedule');
  const [draft, setDraft] = useState<SaveInterviewPayload>(initialDraft);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [historyEditing, setHistoryEditing] = useState(false);
  const [historyDraft, setHistoryDraft] = useState<SaveInterviewPayload>(initialDraft);
  const createdRounds = useMemo(
    () => new Set(schedules.map((schedule) => schedule.interview_round)),
    [schedules],
  );
  const availableRounds = useMemo(
    () => interviewRounds.filter((round) => !createdRounds.has(round)),
    [createdRounds],
  );
  const nextAvailableRound = useMemo(
    () => availableRounds[0] ?? defaultInterviewRound,
    [availableRounds],
  );

  useEffect(() => {
    if (!open) return;
    setActiveTab('schedule');
    setDraft(initialDraft(nextAvailableRound));
    setSelectedId(null);
    setHistoryEditing(false);
    setHistoryDraft(initialDraft());
  }, [candidate?.applicationId, open]);

  useEffect(() => {
    if (!open || activeTab !== 'schedule') return;
    if (!createdRounds.has(draft.interview_round)) return;
    setDraft((currentDraft) => ({
      ...currentDraft,
      interview_round: nextAvailableRound,
    }));
  }, [activeTab, createdRounds, draft.interview_round, nextAvailableRound, open]);

  const selected = useMemo(
    () => schedules.find((schedule) => schedule.id === selectedId) ?? null,
    [schedules, selectedId],
  );
  const isDraftRoundAlreadyCreated = createdRounds.has(draft.interview_round);

  if (!candidate) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isDraftRoundAlreadyCreated) return;
    try {
      await onSave(draft);
    } catch {
      return;
    }
    const currentIndex = interviewRounds.indexOf(draft.interview_round);
    const nextRound =
      interviewRounds.slice(currentIndex + 1).find((round) => !createdRounds.has(round)) ??
      nextAvailableRound;
    setDraft(initialDraft(nextRound));
    setActiveTab('history');
  };

  const selectSchedule = (schedule: InterviewSchedule) => {
    setSelectedId(schedule.id);
    setHistoryEditing(false);
  };

  const editSchedule = (schedule: InterviewSchedule) => {
    setHistoryDraft(scheduleToDraft(schedule));
    setHistoryEditing(true);
  };

  const submitHistoryEdit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected) return;
    try {
      await onSave(historyDraft, selected.id);
    } catch {
      return;
    }
    setHistoryEditing(false);
  };

  const fieldLabel = 'mb-1.5 block text-[11px] font-semibold uppercase text-text-subtle';
  const controlClass =
    'border-border-subtle/80 bg-background shadow-none focus-visible:border-primary/50 focus-visible:ring-primary/10 time-input';
  const sectionTitle =
    'mb-3 flex items-center gap-2 border-b border-dashed border-border-subtle/80 pb-2 text-[11px] font-semibold uppercase text-text-subtle';

  const historyDetailPanel = selected ? (
    <div className="rounded-2xl border border-border-subtle/80 bg-bg-main/20 p-5">
      <div className="flex items-start justify-between border-b border-border-subtle/60 pb-3">
        <div>
          <span
            className={`inline-flex h-6 min-w-12 items-center justify-center rounded border px-2 text-[10px] font-bold ${getInterviewRoundBadgeClass(
              selected.interview_result,
            )}`}
          >
            {roundCode(selected.interview_round)}
          </span>
          <div className="mt-2 text-sm font-bold text-text-main">
            {selected.interview_type === 'ONLINE' ? 'Online' : 'Offline'}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 border-primary/20 px-2 text-[10px] text-primary shadow-none"
            onClick={() => editSchedule(selected)}
          >
            <Pencil size={12} /> Edit
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-border-subtle/70 bg-background p-3">
          <div className="text-[10px] font-bold uppercase text-text-subtle">Start time</div>
          <div className="mt-1 flex items-center gap-1.5 text-sm font-bold">
            <Clock3 size={13} className="text-text-subtle" />
            {formatScheduleTime(selected.start_at)} ({selected.duration_minutes} mins)
          </div>
        </div>
        <div className="rounded-lg border border-border-subtle/70 bg-background p-3">
          <div className="text-[10px] font-bold uppercase text-text-subtle">Interview date</div>
          <div className="mt-1 flex items-center gap-1.5 text-sm font-bold">
            <CalendarDays size={13} className="text-text-subtle" />
            {formatScheduleDate(selected.start_at)}
          </div>
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-border-subtle/70 bg-background p-3">
        <div className="text-[10px] font-bold uppercase text-text-subtle">Interviewer</div>
        <div className="mt-1 flex items-center gap-1.5 text-sm font-bold">
          <UserRound size={13} className="text-text-subtle" />
          {(selected.interviewers?.length ? selected.interviewers : [selected.interviewer])
            .map((interviewer) => interviewer.display_name)
            .join(', ')}
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-border-subtle/70 bg-background p-3">
        <div className="text-[10px] font-bold uppercase text-text-subtle">Meeting room</div>
        <div className="mt-1 flex items-center gap-1.5 text-sm font-bold">
          <MapPin size={13} className="text-text-subtle" />
          {selected.meeting_room || 'N/A'}
        </div>
      </div>
      <div className="mt-3 rounded-lg border border-border-subtle/70 bg-background p-3">
        <div className="text-[10px] font-bold uppercase text-text-subtle">Meeting link</div>
        {selected.meeting_link ? (
          <a
            href={selected.meeting_link}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            <Link2 size={13} />
            Join Microsoft Teams
            <ExternalLink size={12} />
          </a>
        ) : (
          <div className="mt-1 flex items-center gap-1.5 text-sm text-text-subtle">
            <Link2 size={13} /> N/A
          </div>
        )}
      </div>
      <div className="mt-3 rounded-lg border border-border-subtle/70 bg-background p-3">
        <div className="text-[10px] font-bold uppercase text-text-subtle">Note</div>
        <div className="mt-1 whitespace-pre-wrap text-sm italic text-text-subtle">
          {selected.note || 'N/A'}
        </div>
      </div>
      {selected.graph_sync_error ? (
        <p className="mt-3 text-xs text-destructive">{selected.graph_sync_error}</p>
      ) : null}
    </div>
  ) : null;

  const historyEditPanel = selected ? (
    <form
      onSubmit={submitHistoryEdit}
      className="rounded-2xl border border-border-subtle/80 bg-bg-main/20 p-5"
    >
      <div className="flex items-center justify-between border-b border-border-subtle/60 pb-3">
        <div className="flex items-center gap-3 text-sm font-bold">
          <button
            type="button"
            className="text-text-subtle hover:text-primary"
            onClick={() => setHistoryEditing(false)}
            aria-label="Back to interview details"
          >
            <ArrowLeft size={16} />
          </button>
          Edit Interview ({roundCode(selected.interview_round)})
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label>
          <span className={fieldLabel}>Interview round</span>
          <Select
            value={historyDraft.interview_round}
            onValueChange={(value) => setHistoryDraft({ ...historyDraft, interview_round: value })}
          >
            <SelectTrigger className={`w-full ${controlClass}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {interviewRounds.map((round) => (
                <SelectItem key={round} value={round}>
                  {round}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label>
          <span className={fieldLabel}>Interview type</span>
          <Select
            value={historyDraft.interview_type}
            onValueChange={(value: 'ONLINE' | 'OFFLINE') =>
              setHistoryDraft({ ...historyDraft, interview_type: value })
            }
          >
            <SelectTrigger className={`w-full ${controlClass}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ONLINE">Online</SelectItem>
              <SelectItem value="OFFLINE">Offline</SelectItem>
            </SelectContent>
          </Select>
        </label>
        <label>
          <span className={fieldLabel}>Interview date</span>
          <Input
            className={controlClass}
            type="date"
            required
            value={historyDraft.interview_date}
            onChange={(event) =>
              setHistoryDraft({ ...historyDraft, interview_date: event.target.value })
            }
          />
        </label>
        <label>
          <span className={fieldLabel}>Start time</span>
          <Input
            className={controlClass}
            type="time"
            required
            value={historyDraft.start_time}
            onChange={(event) =>
              setHistoryDraft({ ...historyDraft, start_time: event.target.value })
            }
          />
        </label>
        <label>
          <span className={fieldLabel}>Duration</span>
          <Select
            value={String(historyDraft.duration_minutes)}
            onValueChange={(value) =>
              setHistoryDraft({ ...historyDraft, duration_minutes: Number(value) })
            }
          >
            <SelectTrigger className={`w-full ${controlClass}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[30, 45, 60, 90].map((duration) => (
                <SelectItem key={duration} value={String(duration)}>
                  {duration} minutes
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label>
          <span className={fieldLabel}>Interviewer</span>
          <InterviewerCombobox
            interviewers={interviewers}
            value={historyDraft.interviewer_user_ids}
            onChange={(value) => setHistoryDraft({ ...historyDraft, interviewer_user_ids: value })}
            className={controlClass}
          />
        </label>
      </div>

      <label className="mt-3 block">
        <span className={fieldLabel}>Meeting room</span>
        <Select
          required={historyDraft.interview_type === 'OFFLINE'}
          value={historyDraft.meeting_room ?? ''}
          onValueChange={(value) => setHistoryDraft({ ...historyDraft, meeting_room: value })}
        >
          <SelectTrigger className={`w-full ${controlClass}`}>
            <SelectValue placeholder="Select meeting room" />
          </SelectTrigger>
          <SelectContent>
            {meetingRooms.map((room) => (
              <SelectItem key={room} value={room}>
                {room}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="mt-3 block">
        <span className={fieldLabel}>Meeting link</span>
        <Input
          className={controlClass}
          readOnly
          value={selected.meeting_link ?? ''}
          placeholder="Microsoft Teams link is generated after saving"
        />
      </label>
      <label className="mt-3 block">
        <span className={fieldLabel}>Note</span>
        <Textarea
          className={`${controlClass} min-h-16 resize-none`}
          value={historyDraft.note ?? ''}
          onChange={(event) => setHistoryDraft({ ...historyDraft, note: event.target.value })}
        />
      </label>
      <div className="mt-4 flex justify-end gap-2 border-t border-border-subtle/60 pt-3">
        <Button type="button" variant="ghost" size="sm" onClick={() => setHistoryEditing(false)}>
          Back
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={saving || historyDraft.interviewer_user_ids.length === 0}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-2rem)] max-w-3xl gap-0 overflow-hidden border-border-subtle/80 p-0">
        <DialogHeader className="border-b border-border-subtle/60 px-6 py-4 pr-14">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-success/30 bg-success-muted text-success-muted-foreground">
              <CalendarDays size={20} />
            </div>
            <DialogTitle className="text-base">Schedule &amp; Manage Interviews</DialogTitle>
            <DialogDescription className="sr-only">
              Manage interviews for {candidate.name}
            </DialogDescription>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="min-h-0 gap-0">
          <TabsList className="h-[52px] w-full justify-start rounded-none border-b border-border-subtle/60 bg-background px-6 p-0">
            <TabsTrigger
              value="schedule"
              className="h-[52px] flex-none rounded-none border-0 border-b-2 border-transparent bg-transparent px-4 text-xs font-semibold text-text-subtle shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              <CalendarDays size={15} />
              Set interview
            </TabsTrigger>
            <TabsTrigger
              value="history"
              className="h-[52px] flex-none rounded-none border-0 border-b-2 border-transparent bg-transparent px-4 text-xs font-semibold text-text-subtle shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              <History size={15} />
              Interview History
              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
                {schedules.length}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="schedule" className="max-h-[calc(100vh-10rem)] overflow-y-auto">
            <form onSubmit={submit}>
              <div className="space-y-5 px-6 py-5">
                <section>
                  <div className={sectionTitle}>
                    <UserRound size={14} className="text-primary" />
                    Candidate information
                  </div>
                  <div className="grid gap-4 rounded-lg border border-border-subtle/80 bg-bg-main/70 px-4 py-3 sm:grid-cols-3">
                    {[
                      ['Position', candidate.role],
                      ['Candidate name', candidate.name],
                      ['Email', candidate.email || 'Not provided'],
                    ].map(([label, value]) => (
                      <div key={label} className="min-w-0">
                        <div className="text-[10px] font-semibold uppercase text-text-subtle">
                          {label}
                        </div>
                        <div className="mt-1 truncate text-xs font-semibold">{value}</div>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <div className={sectionTitle}>
                    <CalendarDays size={14} className="text-primary" />
                    Interview schedule
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <label>
                      <span className={fieldLabel}>Interview round</span>
                      <Select
                        value={draft.interview_round}
                        onValueChange={(value) => setDraft({ ...draft, interview_round: value })}
                      >
                        <SelectTrigger className={`w-full ${controlClass}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {interviewRounds.map((round) => (
                            <SelectItem
                              key={round}
                              value={round}
                              disabled={createdRounds.has(round)}
                            >
                              {round}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </label>
                    <label>
                      <span className={fieldLabel}>Interview type</span>
                      <Select
                        value={draft.interview_type}
                        onValueChange={(value: 'ONLINE' | 'OFFLINE') =>
                          setDraft({ ...draft, interview_type: value })
                        }
                      >
                        <SelectTrigger className={`w-full ${controlClass}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ONLINE">Online</SelectItem>
                          <SelectItem value="OFFLINE">Offline</SelectItem>
                        </SelectContent>
                      </Select>
                    </label>
                    <label>
                      <span className={fieldLabel}>Interview date</span>
                      <Input
                        className={controlClass}
                        required
                        type="date"
                        min={localDate(new Date())}
                        value={draft.interview_date}
                        onChange={(event) =>
                          setDraft({ ...draft, interview_date: event.target.value })
                        }
                      />
                    </label>
                    <label>
                      <span className={fieldLabel}>Start time</span>
                      <Input
                        className={controlClass}
                        required
                        type="time"
                        value={draft.start_time}
                        onChange={(event) => setDraft({ ...draft, start_time: event.target.value })}
                      />
                    </label>
                    <label>
                      <span className={fieldLabel}>Duration</span>
                      <Select
                        value={String(draft.duration_minutes)}
                        onValueChange={(value) =>
                          setDraft({ ...draft, duration_minutes: Number(value) })
                        }
                      >
                        <SelectTrigger className={`w-full ${controlClass}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[30, 45, 60, 90].map((duration) => (
                            <SelectItem key={duration} value={String(duration)}>
                              {duration} minutes
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </label>
                    <label>
                      <span className={fieldLabel}>Interviewer</span>
                      <InterviewerCombobox
                        interviewers={interviewers}
                        value={draft.interviewer_user_ids}
                        onChange={(value) => setDraft({ ...draft, interviewer_user_ids: value })}
                        className={controlClass}
                      />
                    </label>
                  </div>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <label>
                      <span className={fieldLabel}>Meeting room</span>
                      <Select
                        required={draft.interview_type === 'OFFLINE'}
                        value={draft.meeting_room ?? ''}
                        onValueChange={(value) => setDraft({ ...draft, meeting_room: value })}
                      >
                        <SelectTrigger className={`w-full ${controlClass}`}>
                          <div className="flex min-w-0 items-center gap-2">
                            <MapPin className="size-4 shrink-0 text-text-subtle" />
                            <SelectValue placeholder="Select meeting room" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {meetingRooms.map((room) => (
                            <SelectItem key={room} value={room}>
                              {room}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </label>
                    <label>
                      <span className={fieldLabel}>Meeting link</span>
                      <div className="relative">
                        <Video
                          className="pointer-events-none absolute left-3 top-2.5 text-text-subtle"
                          size={15}
                        />
                        <Input
                          className={`${controlClass} pl-9`}
                          readOnly
                          value=""
                          placeholder={
                            draft.interview_type === 'ONLINE'
                              ? 'Microsoft Teams link is generated after saving'
                              : 'Not applicable for offline interviews'
                          }
                        />
                      </div>
                    </label>
                  </div>
                </section>

                <section>
                  <div className={sectionTitle}>
                    <NotebookText size={14} className="text-primary" />
                    Additional information
                  </div>
                  <label>
                    <span className={fieldLabel}>Note</span>
                    <Textarea
                      className={`${controlClass} min-h-20 resize-none`}
                      value={draft.note ?? ''}
                      onChange={(event) => setDraft({ ...draft, note: event.target.value })}
                      placeholder="HR notes, interview focus, company address..."
                    />
                  </label>
                </section>
              </div>
              <div className="flex justify-end gap-2 border-t border-border-subtle/60 px-6 py-4">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    saving || draft.interviewer_user_ids.length === 0 || isDraftRoundAlreadyCreated
                  }
                  className="gap-2"
                >
                  <Check size={15} />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="history" className="min-h-[470px]">
            {loading ? (
              <div className="grid min-h-[470px] place-items-center text-sm text-text-subtle">
                Loading interview schedules...
              </div>
            ) : schedules.length === 0 ? (
              <div className="grid min-h-[470px] place-items-center text-sm text-text-subtle">
                No interviews scheduled.
              </div>
            ) : (
              <div className="grid min-h-[470px] gap-5 p-5 md:grid-cols-[288px_1fr]">
                <div className="border-r border-border-subtle/60 pr-4">
                  <div className="mb-2 text-[10px] font-bold uppercase text-text-subtle">
                    Rounds list
                  </div>
                  <div className="space-y-2">
                    {schedules.map((schedule) => (
                      <button
                        key={schedule.id}
                        type="button"
                        onClick={() => selectSchedule(schedule)}
                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${
                          selected?.id === schedule.id
                            ? 'border-primary/60 bg-primary-light'
                            : 'border-border-subtle/80 bg-background hover:border-primary/40'
                        }`}
                      >
                        <span
                          className={`grid size-6 shrink-0 place-items-center rounded-md border text-[10px] font-bold ${getInterviewRoundBadgeClass(
                            schedule.interview_result,
                          )}`}
                        >
                          {roundCode(schedule.interview_round)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-bold text-text-main">
                            {roundCode(schedule.interview_round)}
                          </span>
                          <span className="mt-1 flex items-center gap-1 text-[10px] text-text-subtle">
                            <CalendarDays size={11} />
                            {formatScheduleDate(schedule.start_at)} @{' '}
                            {formatScheduleTime(schedule.start_at)}
                          </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-1">
                          <span className="rounded bg-bg-main px-1.5 py-0.5 text-[9px] font-semibold text-text-subtle">
                            {schedule.interview_type === 'ONLINE' ? 'Online' : 'Offline'}
                          </span>
                          <ChevronRight size={14} className="text-text-subtle/60" />
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                {!selected ? (
                  <div className="rounded-2xl border border-border-subtle/80 bg-bg-main/20 p-5">
                    <div className="grid h-full min-h-[390px] place-items-center rounded-xl border border-dashed border-border-subtle/80 bg-background">
                      <div className="text-center">
                        <CalendarDays className="mx-auto text-text-subtle/50" size={25} />
                        <div className="mt-3 text-sm font-semibold text-text-subtle">
                          Select an Interview
                        </div>
                        <div className="mt-1 text-xs text-text-subtle/70">
                          Choose from the list on the left to view full details or edit.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : historyEditing ? (
                  historyEditPanel
                ) : (
                  historyDetailPanel
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
